/**
 * SubscriptionBanner.js
 *
 * Global expiration reminder banner.
 *
 * Calls GET /subscription-alert on mount. When show_reminder === true, renders
 * a fixed red banner at the top of the page with a "Renew Now" CTA that
 * navigates the user to the Plans section.
 *
 * Never shown when:
 *   - has_payment_source === true (auto-renewal handles it)
 *   - Trial plan
 *   - No subscription
 *
 * Usage: <SubscriptionBanner onRenewClick={fn} />
 *   onRenewClick — optional; if provided, called instead of navigate.
 *   Typically: () => setActiveView("Planes")
 */

import { useEffect, useState, useCallback } from "react";
import { makeRequest } from "../assets/functions_helper";

const FONT = "Como, sans-serif";
const BANNER_BG = "#C0392B";

function getBannerMessage(alert) {
  if (!alert) return "";
  if (alert.subscription_status === "PendingPayment")
    return "🔴 Tu renovación está pendiente. Completa el pago para restaurar el acceso completo.";
  if (alert.subscription_status === "PaymentError")
    return "🔴 Tu último pago falló. Renueva para recuperar el acceso.";
  if (alert.days_until_expiry === 0)
    return "🔴 Tu suscripción vence hoy. Renueva inmediatamente.";
  if (alert.days_until_expiry > 0)
    return `⚠ Tu suscripción vence en ${alert.days_until_expiry} día(s). Renueva ahora para evitar interrupciones.`;
  return "⚠ Tu suscripción está por vencer. Renueva ahora.";
}

export function SubscriptionBanner({ onRenewClick }) {
  const [alert, setAlert] = useState(null);

  const loadAlert = useCallback(async () => {
    try {
      const data = await makeRequest(
        "GET",
        "subscriptions_router/subscription-alert",
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        true // skipLoader — silent background call
      );
      setAlert(data);
    } catch (_) {
      // Non-critical — swallow silently; don't block the app
    }
  }, []);

  useEffect(() => {
    loadAlert();
  }, [loadAlert]);

  // Apply body padding when banner is visible so content isn't hidden under it
  useEffect(() => {
    if (alert?.show_reminder) {
      document.body.style.paddingTop = "56px";
    } else {
      document.body.style.paddingTop = "";
    }
    return () => { document.body.style.paddingTop = ""; };
  }, [alert?.show_reminder]);

  if (!alert?.show_reminder) return null;

  const message = getBannerMessage(alert);

  const handleRenew = () => {
    if (typeof onRenewClick === "function") {
      onRenewClick();
    }
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 99999,
        background: BANNER_BG,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 24px",
        fontSize: "15px",
        fontWeight: 700,
        fontFamily: FONT,
        gap: "16px",
        boxShadow: "0 2px 16px rgba(192,57,43,0.35)",
        flexWrap: "wrap",
      }}
    >
      <span style={{ flex: 1, minWidth: "200px", lineHeight: 1.45 }}>
        {message}
      </span>

      <button
        id="subscription-banner-renew-btn"
        onClick={handleRenew}
        style={{
          background: "#fff",
          color: BANNER_BG,
          border: "none",
          padding: "8px 20px",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: FONT,
          flexShrink: 0,
          transition: "filter 0.2s ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.filter = "brightness(0.95)")}
        onMouseLeave={(e) => (e.currentTarget.style.filter = "brightness(1)")}
      >
        Renovar ahora
      </button>
    </div>
  );
}
